import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import { MdPrint, MdLogout, MdQrCode2, MdArrowBack } from "react-icons/md";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import logo from "../../assets/logo.png";
import useStore from "../../utils/store";
import { getCustomerOwnQrTemplate } from "../../utils/api";

const computeSequentialSerial = (baseValue, index = 0) => {
  const str = String(baseValue || "1").trim();
  if (!str) return (index + 1).toString();
  const match = str.match(/^(.*?)(\d+)$/);
  if (match) {
    const prefix = match[1];
    const numStr = match[2];
    const startNum = parseInt(numStr, 10);
    const currentNum = startNum + index;
    const padded = String(currentNum).padStart(numStr.length, "0");
    return `${prefix}${padded}`;
  }
  return `${str}${index + 1}`;
};

const countLabelsInRange = (startStr, endStr) => {
  const s = String(startStr || "").trim();
  const e = String(endStr || "").trim();
  if (!s || !e) return 0;
  const sMatch = s.match(/^(.*?)(\d+)$/);
  const eMatch = e.match(/^(.*?)(\d+)$/);
  if (sMatch && eMatch) {
    if (sMatch[1] !== eMatch[1]) return 0;
    const sNum = parseInt(sMatch[2], 10);
    const eNum = parseInt(eMatch[2], 10);
    if (isNaN(sNum) || isNaN(eNum) || eNum < sNum) return 0;
    return eNum - sNum + 1;
  }
  const sNum = parseInt(s, 10);
  const eNum = parseInt(e, 10);
  if (!isNaN(sNum) && !isNaN(eNum)) {
    if (eNum < sNum) return 0;
    return eNum - sNum + 1;
  }
  return 0;
};

const DEFAULT_STANDARD_FIELDS = [
  { id: "name", label: "Name", printOnLabel: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 8, fontSize: 13, fontWeight: "bold" },
  { id: "agency", label: "Agency", printOnLabel: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 22, fontSize: 11, fontWeight: "normal" },
  { id: "center", label: "Center", printOnLabel: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 35, fontSize: 11, fontWeight: "normal" },
  { id: "cropYear", label: "Crop year", printOnLabel: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 48, fontSize: 11, fontWeight: "normal" },
  { id: "commodity", label: "Commodity", printOnLabel: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 61, fontSize: 11, fontWeight: "normal" },
  { id: "fssai", label: "Fssai LIC no", printOnLabel: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 74, fontSize: 10, fontWeight: "normal" },
  { id: "srNo", label: "Sr. No", printOnLabel: true, printPrefix: true, showToCustomer: true, defaultValue: "1", x: 4, y: 87, fontSize: 12, fontWeight: "bold" },
];

const QrPortal = () => {
  const user = useStore((state) => state.user);
  const setUser = useStore((state) => state.setUser);
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Fill Details, 2: Preview & Print

  const [formValues, setFormValues] = useState({});
  const [srStart, setSrStart] = useState(1);
  const [srEnd, setSrEnd] = useState(55);

  useEffect(() => {
    if (!user || user?.type !== "Customer") {
      navigate("/login");
      return;
    }
    fetchTemplate();
  }, [user]);

  const fetchTemplate = async () => {
    setLoading(true);
    if (user?.customer?.qrTemplate && user.customer.qrTemplate.length) {
      applyTemplate(user.customer.qrTemplate, user.customer);
      setLoading(false);
    } else {
      const [success, data] = await getCustomerOwnQrTemplate(user?.token);
      if (success && data?.customer?.qrTemplate) {
        applyTemplate(data.customer.qrTemplate, data.customer);
      } else {
        applyTemplate(
          {
            length: 100,
            height: 50,
            up: 1,
            qrX: 68,
            qrY: 14,
            qrDimension: 82,
            borderStyle: "standard",
            fields: DEFAULT_STANDARD_FIELDS,
          },
          user?.customer
        );
      }
      setLoading(false);
    }
  };

  const applyTemplate = (tmpl, cust) => {
    const rawFields = (tmpl.fields && tmpl.fields.length > 0) ? tmpl.fields : DEFAULT_STANDARD_FIELDS;
    const activeFields = rawFields.map((f, i) => ({
      ...f,
      printOnLabel: f.printOnLabel !== undefined ? f.printOnLabel : true,
      x: f.x !== undefined ? f.x : 4,
      y: f.y !== undefined ? f.y : Math.round(8 + i * 13),
      fontSize: f.fontSize || 11,
      fontWeight: f.fontWeight || "normal",
    }));

    const finalTmpl = {
      ...tmpl,
      length: tmpl.length || 100,
      height: tmpl.height || 50,
      up: tmpl.up || 1,
      qrX: tmpl.qrX !== undefined ? tmpl.qrX : tmpl.qrPosition === "left" ? 5 : 68,
      qrY: tmpl.qrY !== undefined ? tmpl.qrY : 14,
      qrDimension: tmpl.qrDimension || 82,
      printQr: tmpl.printQr !== undefined ? Boolean(tmpl.printQr) : true,
      borderStyle: tmpl.borderStyle || "standard",
      fields: activeFields,
    };
    setTemplate(finalTmpl);

    const initialVals = {};
    activeFields.forEach((f) => {
      initialVals[f.id] = f.defaultValue || "";
    });

    if (!initialVals.name && (cust?.name || cust?.userName)) {
      initialVals.name = cust.name || cust.userName;
    }
    if (!initialVals.agency && cust?.agency) initialVals.agency = cust.agency;
    if (!initialVals.fssai && cust?.fssai) initialVals.fssai = cust.fssai;
    if (!initialVals.center && cust?.location) initialVals.center = cust.location;

    setFormValues(initialVals);
    const defSr = (tmpl.fields || []).find(f => f.id === 'srNo')?.defaultValue || "1";
    setSrStart(defSr);
    setSrEnd(computeSequentialSerial(defSr, 54));
  };

  const handleInputChange = (id, val) => {
    setFormValues((prev) => ({ ...prev, [id]: val }));
  };

  const handleProceedToPreview = () => {
    const total = countLabelsInRange(srStart, srEnd);
    if (!srStart || !srEnd || total < 1) {
      toast.error("Please enter a valid Sr. No range (Start must be <= End)");
      return;
    }
    if (total > 500) {
      toast.error("Maximum 500 labels can be generated at a time.");
      return;
    }
    setStep(2);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleLogout = () => {
    setUser(null);
    navigate("/login");
  };

  const activeFieldsList =
    template?.fields && template.fields.length > 0
      ? template.fields
      : DEFAULT_STANDARD_FIELDS;

  // Filter fields that are enabled for TEXT printing on the label
  const printableFields = activeFieldsList.filter((f) => f.printOnLabel !== false);

  const generateLabels = () => {
    if (!template) return [];
    const labels = [];
    const total = countLabelsInRange(srStart, srEnd);

    for (let i = 0; i < total; i++) {
      const currentSr = computeSequentialSerial(srStart, i);
      const fieldLines = [];
      const qrLines = [];

      // 1. Visible Text Lines on physical label (only fields with printOnLabel !== false)
      printableFields.forEach((f) => {
        let val = "";
        if (f.id === "srNo") {
          val = currentSr;
        } else if (f.showToCustomer) {
          val = formValues[f.id] !== undefined && formValues[f.id] !== "" ? formValues[f.id] : f.defaultValue || "";
        } else {
          val = f.defaultValue || "";
        }

        const formatted = f.printPrefix ? `${f.label}: ${val}` : val;
        fieldLines.push({
          id: f.id,
          label: f.label,
          text: formatted,
          val: val,
          type: f.type || "text",
          imageWidth: f.imageWidth || 40,
          imageHeight: f.imageHeight || 40,
          x: f.x !== undefined ? f.x : 4,
          y: f.y !== undefined ? f.y : 8,
          fontSize: f.fontSize || 11,
          fontWeight: f.fontWeight || "normal",
        });
      });

      // 2. QR Code Content: ALWAYS includes ALL fields with full data!
      // Even if text print is disabled, QR Code keeps 100% of the fields!
      activeFieldsList.forEach((f) => {
        if (f.includeInQr === false) return;
        let val = "";
        if (f.id === "srNo") {
          val = currentSr.toString();
        } else if (formValues[f.id] !== undefined && formValues[f.id] !== "") {
          val = formValues[f.id];
        } else {
          val = f.defaultValue || "";
        }
        if (f.type === "image") {
          if (val && !val.startsWith("data:")) {
            qrLines.push(`${f.label}: ${val}`);
          }
        } else {
          if (f.id === "srNo" || f.printPrefix === false) {
            qrLines.push(val);
          } else {
            qrLines.push(`${f.label}: ${val}`);
          }
        }
      });

      labels.push({
        srNo: currentSr,
        fields: fieldLines,
        qrText: qrLines.join("\n"),
      });
    }

    return labels;
  };

  const labels = step === 2 ? generateLabels() : [];
  const lengthMm = template?.length || 100;
  const heightMm = template?.height || 50;
  const upCount = template?.up || 1;
  const qrX = template?.qrX !== undefined ? template.qrX : 68;
  const qrY = template?.qrY !== undefined ? template.qrY : 14;
  const qrDimension = template?.qrDimension || 82;
  const borderStyle = template?.borderStyle || "standard";

  const getComputedBorderStyle = (bStyle) => {
    if (bStyle === "none") return "border-0";
    if (bStyle === "thin") return "border border-black";
    if (bStyle === "dotted") return "border-2 border-dotted border-black";
    return "border-2 border-black";
  };

  // Preview label dimensions
  const previewLabelWidthPx = Math.min(380, Math.max(280, lengthMm * 3.4));
  const previewLabelHeightPx = Math.round((previewLabelWidthPx * heightMm) / lengthMm);
  const scale = previewLabelWidthPx / 480;

  // Customer editable fields (show if showToCustomer is true, even if text print is disabled so it can be in QR!)
  const customerFields = activeFieldsList.filter(
    (f) => f.showToCustomer && f.id !== "srNo"
  );

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #qr-printable-area, #qr-printable-area * {
            visibility: visible !important;
          }
          #qr-printable-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .no-print {
            display: none !important;
          }
          @page {
            size: ${lengthMm * upCount}mm ${heightMm}mm;
            margin: 0mm;
          }
          .print-label-card {
            width: ${lengthMm}mm !important;
            height: ${heightMm}mm !important;
            position: relative !important;
            box-sizing: border-box !important;
            page-break-inside: avoid !important;
            overflow: hidden !important;
          }
          .print-qr-code {
            position: absolute !important;
            left: ${qrX}% !important;
            top: ${qrY}% !important;
          }
        }
      `}</style>

      {/* Standalone Header */}
      <header className="bg-slate-900 text-white shadow-md px-6 py-3.5 flex justify-between items-center no-print">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Prabhu Labels" className="h-9 w-auto object-contain bg-white/10 p-1 rounded-md" />
          <div>
            <h1 className="text-base md:text-lg font-black tracking-wide text-white leading-tight">
              PRABHU LABELS
            </h1>
            <span className="text-[11px] text-emerald-400 font-semibold uppercase tracking-wider block">
              Customer QR Label Generator Portal
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <span className="text-xs text-slate-400 block">Logged In As</span>
            <span className="text-sm font-bold text-slate-100">
              {user?.customer?.name || user?.customer?.userName || user?.userName || "Customer"}
            </span>
          </div>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleLogout}
            startIcon={<MdLogout />}
            className="text-xs font-bold border-red-500/50 text-red-300 hover:bg-red-500/10"
          >
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-5xl w-full mx-auto p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-3"></div>
              <p className="text-sm font-semibold">Loading your label setup...</p>
            </div>
          </div>
        ) : step === 1 ? (
          /* Step 1: Data Entry */
          <div className="flex flex-col gap-6">
            {/* Banner */}
            <div className="bg-gradient-to-r from-emerald-800 to-teal-900 rounded-2xl p-6 text-white shadow-md">
              <div className="inline-flex items-center gap-2 bg-emerald-700/60 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-emerald-200 mb-2">
                <MdQrCode2 className="text-base" /> QR Label Printing
              </div>
              <h2 className="text-xl md:text-2xl font-black text-white">
                Generate Customer QR Labels
              </h2>
              <p className="text-emerald-100 text-xs md:text-sm mt-1 font-medium">
                Fill in your details below, specify your serial number range, and generate printable labels.
              </p>
              <div className="mt-3.5 inline-flex items-center gap-3 bg-black/25 text-white border border-white/20 px-3.5 py-1.5 rounded-lg text-xs font-semibold">
                <span>📏 Label Size: <strong className="text-amber-300">{lengthMm} mm × {heightMm} mm</strong></span>
                <span>•</span>
                <span>📑 Layout: <strong className="text-amber-300">{upCount} Up ({upCount} pc per row)</strong></span>
                <span>•</span>
                <span>🏷️ Printed Text Fields: <strong className="text-emerald-300">{printableFields.length}</strong></span>
                <span>•</span>
                <span>📱 QR Data Fields: <strong className="text-cyan-300">{activeFieldsList.length} (Full Data)</strong></span>
              </div>
            </div>

            {/* Input Form Card */}
            <Card className="shadow-sm border border-slate-200 rounded-2xl overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
                <h3 className="text-sm font-bold text-slate-800">
                  Step 1: Label Details & Serial Number Range
                </h3>
              </div>
              <CardContent className="p-6 flex flex-col gap-6">
                {/* Dynamic Fields */}
                {customerFields.length > 0 ? (
                  <div>
                    <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider mb-3">
                      Label Information (Encoded into QR Code)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {customerFields.map((f) => {
                        if (f.type === "image") {
                          return (
                            <div key={f.id} className="flex flex-col gap-1.5 border border-slate-200 p-3 rounded-xl bg-slate-50">
                              <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                                <span>🖼️ {f.label}</span>
                                {formValues[f.id] && <span className="text-[10px] text-emerald-600 font-bold">Image Set</span>}
                              </label>
                              <div className="flex items-center gap-3">
                                {formValues[f.id] ? (
                                  <img src={formValues[f.id]} alt={f.label} className="w-12 h-12 object-contain rounded border border-slate-300 bg-white" />
                                ) : (
                                  <div className="w-12 h-12 rounded border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">
                                    No Img
                                  </div>
                                )}
                                <label className="text-xs font-bold bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-3 py-1.5 rounded-lg cursor-pointer transition">
                                  Upload Image
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const reader = new FileReader();
                                        reader.onload = (ev) => handleInputChange(f.id, ev.target.result);
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                  />
                                </label>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <TextField
                            key={f.id}
                            label={f.label}
                            type={f.type === "number" ? "number" : "text"}
                            value={formValues[f.id] || ""}
                            onChange={(e) => handleInputChange(f.id, e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                          />
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 font-medium">
                    ℹ️ All printable label fields are pre-configured by Prabhu Labels. Please specify your serial number range below to proceed.
                  </div>
                )}

                {/* Serial Number Range */}
                <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-5">
                  <h4 className="text-sm font-black text-emerald-950 mb-1 flex items-center gap-1.5">
                    <span>🔢</span> Serial Number Range (Sr. No)
                  </h4>
                  <p className="text-xs text-emerald-900 font-medium mb-4">
                    Enter the starting and ending serial numbers. A separate label with its own unique QR code will be generated for each serial number in this range.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TextField
                      label="Start Serial Number (e.g. Abc123 or 1)"
                      type="text"
                      value={srStart}
                      onChange={(e) => setSrStart(e.target.value)}
                      fullWidth
                      size="small"
                      variant="outlined"
                      className="bg-white rounded"
                    />
                    <TextField
                      label="End Serial Number (e.g. Abc177 or 55)"
                      type="text"
                      value={srEnd}
                      onChange={(e) => setSrEnd(e.target.value)}
                      fullWidth
                      size="small"
                      variant="outlined"
                      className="bg-white rounded"
                    />
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs font-semibold text-emerald-950">
                    <span>
                      Total labels to generate:{" "}
                      <strong className="text-sm font-black text-emerald-950">
                        {countLabelsInRange(srStart, srEnd)} Labels
                      </strong>
                    </span>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleProceedToPreview}
                    className="font-bold px-8 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white shadow-md"
                  >
                    PREVIEW LABELS ({countLabelsInRange(srStart, srEnd)})
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Step 2: Multi-Label Preview & Print */
          <div className="flex flex-col gap-5">
            {/* Control Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-wrap justify-between items-center gap-4 no-print">
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                startIcon={<MdArrowBack />}
                className="font-bold text-xs text-slate-800"
              >
                Back to Edit
              </Button>

              <div className="flex items-center gap-3 text-xs text-slate-700 font-semibold">
                <span>
                  Total Labels: <strong className="text-slate-900">{labels.length}</strong> (Sr. {srStart} to {srEnd})
                </span>
                <span>•</span>
                <span>
                  Layout: <strong className="text-slate-900">{upCount} Up</strong> ({lengthMm}mm × {heightMm}mm)
                </span>
                <span>•</span>
                <span className="text-emerald-700 font-bold">
                  QR Data: All {activeFieldsList.length} Fields Encoded
                </span>
              </div>

              <Button
                variant="contained"
                color="success"
                onClick={handlePrint}
                startIcon={<MdPrint />}
                size="medium"
                className="font-bold bg-emerald-700 hover:bg-emerald-800 text-white shadow-md px-6"
              >
                Print All ({labels.length})
              </Button>
            </div>

            {/* Printable Preview Area */}
            <div
              id="qr-printable-area"
              className="p-4 bg-slate-200 rounded-2xl overflow-y-auto flex flex-col items-center gap-3"
            >
              <div
                className="grid gap-3 w-full justify-center"
                style={{
                  gridTemplateColumns: `repeat(${upCount}, minmax(0, 1fr))`,
                  maxWidth: `${upCount * (previewLabelWidthPx + 16)}px`,
                }}
              >
                {labels.map((lbl) => (
                  <div
                    key={lbl.srNo}
                    className={`print-label-card bg-white relative overflow-hidden shadow-xs page-break-inside-avoid ${getComputedBorderStyle(
                      borderStyle
                    )}`}
                    style={{
                      width: `${previewLabelWidthPx}px`,
                      height: `${previewLabelHeightPx}px`,
                    }}
                  >
                    {/* Fixed Positioned Dynamic QR Code (only if printQr !== false) */}
                    {template?.printQr !== false && (
                      <div
                        className="print-qr-code absolute bg-white"
                        style={{
                          left: `${qrX}%`,
                          top: `${qrY}%`,
                        }}
                      >
                        <QRCodeSVG
                          value={lbl.qrText}
                          size={Math.round(qrDimension * scale)}
                          level="M"
                        />
                      </div>
                    )}

                    {/* Fixed Positioned Fields (only if enabled on label) */}
                    {lbl.fields.map((f) => {
                      if (f.type === "image") {
                        return (
                          <div
                            key={f.id}
                            className="absolute pointer-events-none"
                            style={{
                              left: `${f.x}%`,
                              top: `${f.y}%`,
                              width: `${Math.round((f.imageWidth || 40) * scale)}px`,
                              height: `${Math.round((f.imageHeight || 40) * scale)}px`,
                            }}
                          >
                            {f.val ? (
                              <img
                                src={f.val}
                                alt={f.label}
                                className="w-full h-full object-contain"
                              />
                            ) : null}
                          </div>
                        );
                      }
                      return (
                        <div
                          key={f.id}
                          className={`absolute font-mono text-black whitespace-nowrap ${
                            f.fontWeight === "bold" ? "font-bold" : "font-normal"
                          }`}
                          style={{
                            left: `${f.x}%`,
                            top: `${f.y}%`,
                            fontSize: `${Math.round(f.fontSize * scale)}px`,
                            lineHeight: 1.15,
                          }}
                        >
                          {f.text}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default QrPortal;
